import React, { useEffect, useState } from "react";
import { Input } from "../ui/input";
import { FormField } from "./FormField";
import {
  DEFAULT_COMPANY_LAB_LIMITS,
  DEFAULT_PADDY_LAB_VALUES,
  PADDY_LAB_METRICS,
  formatLabLimitHint,
} from "../../lib/paddyLabConfig";
import { api } from "../../lib/api";

export { DEFAULT_PADDY_LAB_VALUES };

/**
 * Paddy incoming-grain quality parameters for truck / composite / stack sample lab.
 * Shows company min/max hints and HTML min/max attributes (2 decimal places).
 */
export function PaddyLabAnalysisFields({
  values,
  onChange,
  testIdPrefix = "paddy-lab",
  showNotes = true,
  limits: limitsProp,
}) {
  const [limits, setLimits] = useState(limitsProp || DEFAULT_COMPANY_LAB_LIMITS);

  useEffect(() => {
    if (limitsProp) {
      setLimits(limitsProp);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/lab-validation-limits");
        if (!cancelled && data?.parameter_limits) {
          setLimits(data.parameter_limits);
        }
      } catch {
        /* keep defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [limitsProp]);

  const set = (key) => (e) =>
    onChange({
      ...values,
      [key]: e.target.value,
    });

  return (
    <>
      {PADDY_LAB_METRICS.map((m) => {
        const entry = limits?.[m.key] || {};
        const hint = formatLabLimitHint(limits, m.key, m.suffix);
        const outOfRange =
          values[m.key] !== "" &&
          values[m.key] != null &&
          ((entry.min != null && Number(values[m.key]) < Number(entry.min)) ||
            (entry.max != null && Number(values[m.key]) > Number(entry.max)));
        return (
          <FormField
            key={m.key}
            label={`${m.label}${m.suffix === " mm" ? " (mm)" : ` ${m.suffix}`}`}
            compact
            hint={hint}
          >
            <Input
              type="number"
              step="0.01"
              min={entry.min ?? undefined}
              max={entry.max ?? undefined}
              value={values[m.key] ?? ""}
              onChange={set(m.key)}
              className={outOfRange ? "border-red-500 text-red-700" : undefined}
              data-testid={`${testIdPrefix}-${m.key.replace(/_pct$|_mm$/, "").replace(/_/g, "-")}`}
            />
          </FormField>
        );
      })}

      <FormField label="Optional param 1 name" compact>
        <Input
          value={values.custom_param_1_name || ""}
          onChange={set("custom_param_1_name")}
          placeholder="e.g. Red grain"
          data-testid={`${testIdPrefix}-custom1-name`}
        />
      </FormField>
      <FormField label="Optional param 1 value" compact>
        <Input
          value={values.custom_param_1_value || ""}
          onChange={set("custom_param_1_value")}
          placeholder="Value"
          data-testid={`${testIdPrefix}-custom1-value`}
        />
      </FormField>
      <FormField label="Optional param 2 name" compact>
        <Input
          value={values.custom_param_2_name || ""}
          onChange={set("custom_param_2_name")}
          placeholder="Parameter name"
          data-testid={`${testIdPrefix}-custom2-name`}
        />
      </FormField>
      <FormField label="Optional param 2 value" compact>
        <Input
          value={values.custom_param_2_value || ""}
          onChange={set("custom_param_2_value")}
          placeholder="Value"
          data-testid={`${testIdPrefix}-custom2-value`}
        />
      </FormField>

      {showNotes ? (
        <FormField label="Notes" compact className="sm:col-span-2">
          <Input value={values.notes || ""} onChange={set("notes")} data-testid={`${testIdPrefix}-notes`} />
        </FormField>
      ) : null}
    </>
  );
}
